import { jsPDF } from "jspdf";
import { PDFDocument } from "pdf-lib";
import Intestazione from "../../assets/intestazione-Giuvi-3.png";
import FirmaGiuvi from "../../assets/firmaGiuvi.png";

// --- Blueprint per dichiarazioni su carta intestata ---
// Genera un PDF con intestazione/piè di pagina aziendali (logo e dati societari),
// oggetto, testo del dichiarante, "DICHIARA", corpo della dichiarazione,
// nota allegato, luogo e data e spazio per la firma.
// È possibile concatenare in coda un documento allegato (es. Documento Legale
// Rappresentante: PDF oppure immagine).

const borderColor = [161, 16, 0];

// Dati aziendali mostrati nel piè di pagina (allineati al generatore POS).
const FOOTER_RIGA_1 =
  "GIUVI SRL - Via Belgirate, 15 - 20125 Milano (MI) - Tel. 02/97190315";
const FOOTER_RIGA_2 =
  "Cap.Soc. € 10.000,00 - PI/CF/Nr.Reg.Impr.MI IT07873720960 - REA MI-2010873 - info@giuvi.it";

// Testo base del dichiarante (fonte: dichiarazione.md).
export const TESTO_BASE_DICHIARANTE =
  "Il sottoscritto Ferri Roberto in qualità di legale rappresentante dell’impresa GIUVI SRL con sede in via Belgirate, 15 nel Comune di Milano (prov. di MI), vista la lettera l) dell’allegato XVII del D. Lgs. 81/2008, valendosi delle disposizioni di cui all'art. 46 del D.P.R. 445/2000 e consapevole delle sanzioni penali, nel caso di dichiarazioni non veritiere, di formazione o uso di atti falsi, richiamate nell'art. 76 del D.P.R. 445/2000, ai sensi e per gli effetti dell’art. 47 del D.P.R. 445/2000, sotto la propria personale responsabilità,";

// Nota dell'allegato concatenato in coda alla dichiarazione.
export const NOTA_ALLEGATO =
  "In allegato: copia carta d’identità del dichiarante";

// Markup inline supportato nel corpo della dichiarazione:
// **grassetto** oppure __grassetto__, *corsivo* oppure _corsivo_.
const MARKUP_REGEX = /(\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|_[^_\n]+_)/g;

// Suddivide una riga di testo nei segmenti con il relativo stile.
export function parseInlineSegments(text) {
  const segments = [];
  const source = String(text ?? "");
  let lastIndex = 0;
  let match;

  MARKUP_REGEX.lastIndex = 0;
  while ((match = MARKUP_REGEX.exec(source)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        text: source.slice(lastIndex, match.index),
        bold: false,
        italic: false,
      });
    }
    const token = match[0];
    if (token.startsWith("**") || token.startsWith("__")) {
      segments.push({ text: token.slice(2, -2), bold: true, italic: false });
    } else {
      segments.push({ text: token.slice(1, -1), bold: false, italic: true });
    }
    lastIndex = MARKUP_REGEX.lastIndex;
  }

  if (lastIndex < source.length) {
    segments.push({
      text: source.slice(lastIndex),
      bold: false,
      italic: false,
    });
  }
  return segments;
}

async function loadImageAsDataUrl(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const maxWidth = 800;
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

const formatDataIt = (value) => {
  if (!value) return new Date().toLocaleDateString("it-IT");
  if (value instanceof Date) return value.toLocaleDateString("it-IT");
  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  return raw;
};

/**
 * Genera e scarica una dichiarazione su carta intestata.
 *
 * @param {Object} options
 * @param {string} options.oggetto        Titolo della dichiarazione (dopo "Oggetto:")
 * @param {string} options.corpo          Corpo della dichiarazione (dopo "DICHIARA")
 * @param {string} [options.testoDichiarante] Testo base del dichiarante
 * @param {string} [options.luogo]        Luogo (es. "Milano")
 * @param {string} [options.data]         Data (yyyy-mm-dd, dd/mm/yyyy o Date)
 * @param {{url:string, mimeType?:string, fileName?:string}|null} [options.allegato]
 *        Documento da concatenare in coda (es. Documento Legale Rappresentante)
 * @param {string} [options.fileName]     Nome file di output
 * @param {Function} [options.onReady]    Callback (blob, fileName) per download custom
 * @returns {Promise<{blob: Blob, fileName: string}|null>}
 */
export async function downloadDichiarazionePdf({
  oggetto,
  corpo,
  testoDichiarante = TESTO_BASE_DICHIARANTE,
  luogo = "",
  data = "",
  allegato = null,
  fileName,
  onReady,
}) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  doc.setFont("helvetica", "normal");

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginLeft = 15;
  const marginRight = 15;
  const contentWidth = pageWidth - marginLeft - marginRight;

  const headerDataUrl = await loadImageAsDataUrl(Intestazione);
  const firmaDataUrl = await loadImageAsDataUrl(FirmaGiuvi);

  const addHeader = () => {
    if (headerDataUrl) {
      doc.addImage(headerDataUrl, "JPEG", marginLeft, 10, 50, 0);
    }
  };

  const addFooter = () => {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(100);

      const footerYStart = pageHeight - 18;
      const centerX = pageWidth / 2;
      doc.text(FOOTER_RIGA_1, centerX, footerYStart, { align: "center" });
      doc.text(FOOTER_RIGA_2, centerX, footerYStart + 4, { align: "center" });
      doc.text(
        `Pagina ${i} di ${pageCount}`,
        pageWidth - marginRight,
        pageHeight - 10,
        {
          align: "right",
        },
      );
    }
    doc.setTextColor(0);
  };

  addHeader();
  let y = 45;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - 30) {
      doc.addPage();
      addHeader();
      y = 45;
    }
  };

  const fontStyleFor = (bold, italic) =>
    bold && italic
      ? "bolditalic"
      : bold
        ? "bold"
        : italic
          ? "italic"
          : "normal";

  const applyFontFor = (word, base) => {
    const bold = !!word?.bold || !!base.bold;
    const italic = !!word?.italic || !!base.italic;
    const fontStyle = fontStyleFor(bold, italic);
    doc.setFont("helvetica", fontStyle);
    return fontStyle;
  };

  // Trasforma una riga sorgente in parole con il relativo stile.
  const toStyledWords = (sourceLine) => {
    const words = [];
    parseInlineSegments(sourceLine).forEach((seg) => {
      seg.text
        .split(/\s+/)
        .filter(Boolean)
        .forEach((text) =>
          words.push({ text, bold: !!seg.bold, italic: !!seg.italic }),
        );
    });
    return words;
  };

  // Dispone le parole su più righe rispettando la larghezza del contenuto.
  const wrapWords = (words, base) => {
    const lines = [];
    let current = [];
    let currentWidth = 0;

    words.forEach((word) => {
      applyFontFor(word, base);
      const wordWidth = doc.getTextWidth(word.text);
      applyFontFor(current[current.length - 1] || word, base);
      const gap = current.length ? doc.getTextWidth(" ") : 0;

      if (current.length && currentWidth + gap + wordWidth > contentWidth) {
        lines.push(current);
        current = [word];
        currentWidth = wordWidth;
      } else {
        currentWidth += gap + wordWidth;
        current.push(word);
      }
    });

    if (current.length) lines.push(current);
    return lines;
  };

  // Disegna una riga di parole mantenendo gli stili inline e la spaziatura naturale.
  const drawWords = (lineWords, base) => {
    // Misura ogni parola con il proprio stile per calcolare le larghezze corrette.
    const parts = lineWords.map((word) => {
      const fontStyle = applyFontFor(word, base);
      return { text: word.text, fontStyle, width: doc.getTextWidth(word.text) };
    });

    applyFontFor(lineWords[0], base);
    const spaceWidth = doc.getTextWidth(" ");

    let x = marginLeft;
    parts.forEach((part) => {
      doc.setFont("helvetica", part.fontStyle);
      doc.setTextColor(0);
      doc.text(part.text, x, y);
      x += part.width + spaceWidth;
    });
  };

  // Scrive un blocco di testo mantenendo paragrafi, ritorni a capo e stili inline.
  // Un nuovo paragrafo viene creato solo in presenza di una riga vuota.
  const writeBlock = (
    text,
    { size = 11, style = "normal", after = 4 } = {},
  ) => {
    if (!text) return;
    doc.setFontSize(size);
    doc.setTextColor(0);

    const base = {
      bold: style.includes("bold"),
      italic: style.includes("italic"),
    };
    const lineHeight = size * 0.3528 * 1.35;

    const paragraphs = String(text).split(/\n[ \t]*\n+/);

    paragraphs.forEach((paragraph, pIdx) => {
      // I ritorni a capo singoli all'interno del paragrafo vengono mantenuti.
      const sourceLines = paragraph.split("\n");

      sourceLines.forEach((sourceLine) => {
        const words = toStyledWords(sourceLine);

        if (words.length === 0) {
          ensureSpace(lineHeight + 2);
          y += lineHeight;
          return;
        }

        const wrapped = wrapWords(words, base);
        wrapped.forEach((lineWords) => {
          ensureSpace(lineHeight + 2);
          drawWords(lineWords, base);
          y += lineHeight;
        });
      });

      if (pIdx < paragraphs.length - 1) y += after;
    });
  };

  // --- Oggetto ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0);
  const oggettoLines = doc.splitTextToSize(
    `Oggetto: ${oggetto || ""}`,
    contentWidth,
  );
  oggettoLines.forEach((line) => {
    ensureSpace(6);
    doc.text(line, marginLeft, y);
    y += 6;
  });
  y += 4;

  // --- Testo base del dichiarante ---
  writeBlock(testoDichiarante);
  y += 6;

  // --- DICHIARA (grassetto, centrato) ---
  ensureSpace(14);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(borderColor[0], borderColor[1], borderColor[2]);
  doc.text("DICHIARA", pageWidth / 2, y, { align: "center" });
  y += 12;

  // --- Corpo della dichiarazione ---
  writeBlock(corpo);

  // --- Nota allegato ---
  y += 12;
  ensureSpace(8);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(0);
  const allegatoLines = doc.splitTextToSize(NOTA_ALLEGATO, contentWidth);
  allegatoLines.forEach((line) => {
    ensureSpace(6);
    doc.text(line, marginLeft, y);
    y += 6;
  });

  // --- Luogo e data + firma ---
  // Il blocco viene mantenuto unito e resta sulla pagina corrente se c'è spazio.
  const spazioLuogoData = 14;
  const spazioFirma = 24;
  ensureSpace(spazioLuogoData + spazioFirma + 6);

  y += spazioLuogoData;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(0);
  const luogoData = [luogo, formatDataIt(data)].filter(Boolean).join(", ");
  doc.text(luogoData, pageWidth - marginRight, y, { align: "right" });

  // --- Spazio per la firma ---
  y += spazioFirma;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text("Il Dichiarante", pageWidth - marginRight, y, { align: "right" });
  const firmaWidth = 70;
  if (firmaDataUrl) {
    const firmaImageWidth = 40;
    const firmaImageHeight = 16;
    doc.addImage(
      firmaDataUrl,
      "JPEG",
      pageWidth - marginRight - firmaImageWidth - 10,
      y - 21,
      firmaImageWidth,
      firmaImageHeight,
    );
  }
  doc.setLineWidth(0.3);
  doc.line(
    pageWidth - marginRight - firmaWidth,
    y + 3,
    pageWidth - marginRight,
    y + 3,
  );

  addFooter();

  // --- Unione con l'eventuale allegato ---
  const declarationBytes = doc.output("arraybuffer");
  const mergedPdf = await PDFDocument.create();
  const declPdf = await PDFDocument.load(declarationBytes);
  const declPages = await mergedPdf.copyPages(
    declPdf,
    declPdf.getPageIndices(),
  );
  declPages.forEach((page) => mergedPdf.addPage(page));

  if (allegato?.url) {
    try {
      const res = await fetch(allegato.url);
      if (!res.ok) throw new Error("Download allegato non riuscito");
      const bytes = await res.arrayBuffer();
      const mime = String(allegato.mimeType || "").toLowerCase();
      const name = String(allegato.fileName || allegato.url).toLowerCase();
      const isPdf = mime.includes("pdf") || name.includes(".pdf");

      if (isPdf) {
        const attPdf = await PDFDocument.load(bytes, {
          ignoreEncryption: true,
        });
        const attPages = await mergedPdf.copyPages(
          attPdf,
          attPdf.getPageIndices(),
        );
        attPages.forEach((page) => mergedPdf.addPage(page));
      } else {
        const pageWidthPt = 595.28;
        const pageHeightPt = 841.89;
        const marginPt = 40;
        const page = mergedPdf.addPage([pageWidthPt, pageHeightPt]);
        const image = mime.includes("png")
          ? await mergedPdf.embedPng(bytes)
          : await mergedPdf.embedJpg(bytes);
        const maxW = pageWidthPt - marginPt * 2;
        const maxH = pageHeightPt - marginPt * 2;
        const scale = Math.min(maxW / image.width, maxH / image.height);
        const imgW = image.width * scale;
        const imgH = image.height * scale;
        page.drawImage(image, {
          x: (pageWidthPt - imgW) / 2,
          y: (pageHeightPt - imgH) / 2,
          width: imgW,
          height: imgH,
        });
      }
    } catch (err) {
      console.error("Errore nella concatenazione dell'allegato:", err);
    }
  }

  const finalBytes = await mergedPdf.save();
  const blob = new Blob([finalBytes], { type: "application/pdf" });
  const safeOggetto = String(oggetto || "dichiarazione")
    .replace(/[^a-z0-9]/gi, "_")
    .toLowerCase()
    .slice(0, 60);
  const finalName = fileName || `Dichiarazione_${safeOggetto}.pdf`;

  if (typeof onReady === "function") {
    onReady(blob, finalName);
  } else {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = finalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return { blob, fileName: finalName };
}
